import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';

import {assertAndContinue} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ProcessTaskStatusType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import * as Colors from 'ui-library/Colors';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';

import {RenderTaskStatusWithIcon} from '../shared/ProcedureRunTableUtil';
import {
  StudyInaccessibleContentBanner,
  StudyMissingContentBanner,
  StudyQueryErrorBanner,
} from '../utils/ui/StudyQueryBanners';
import {
  ProcessDevelopmentStudyExecutionProgressQuery,
  ProcessDevelopmentStudyExecutionProgressQueryVariables,
} from './__generated/ProcessDevelopmentStudyExecutionProgressQuery';
import {ProcedureRunParamsTable} from './ProcedureRunParamsTable';
import {
  buildProcessDevelopmentStudyExecutionProgress,
  getUnitOperationCountByTaskStatus,
  PLANNED,
  UnitOperationStatusType,
  ProcedureRunOverview,
  PROCESS_DEVELOPMENT_STUDY_EXECUTION_PROGRESS_QUERY,
} from './useProcessDevelopmentStudyProgress';

export default function ProcessDevelopmentStudyExecutionProgress({studyId}: {studyId: string}): JSX.Element {
  const result = useBenchlingQuery<
    ProcessDevelopmentStudyExecutionProgressQuery,
    ProcessDevelopmentStudyExecutionProgressQueryVariables
  >(PROCESS_DEVELOPMENT_STUDY_EXECUTION_PROGRESS_QUERY, {variables: {studyId}});

  if (result.type === QueryResultType.PENDING) {
    return (
      <>
        <div className={css(styles.loadingDiv)} />
        <HorizontalThrobber />
      </>
    );
  } else if (result.type === QueryResultType.ERROR) {
    return <StudyQueryErrorBanner />;
  } else {
    const {data} = result;
    if (isInaccessible(data.study)) {
      return (
        <StudyInaccessibleContentBanner
          inaccessibleInfo={data.study}
          modelType={ModelTypeConstants.STUDY}
          displayErrorText="This study is no longer accessible. Try refreshing the page if this is unexpected."
        />
      );
    } else if (data.study.procedureRuns.length === 0) {
      return (
        <StudyMissingContentBanner
          parentRenderFunctionName={ProcessDevelopmentStudyExecutionProgress.name}
          modelType={ModelTypeConstants.PROCEDURE_RUN}
          parentId={studyId}
          displayErrorText="The experiment run associated with this study could not be found."
        />
      );
    }
    assertAndContinue(
      data.study.procedureRuns.length === 1 ||
        BenchlingConfig.STUDIES_ENABLE_MULTI_EXPERIMENT_PD_STUDIES === true,
      'single experiment studies only support one procedure run'
    );
    const procedureRun = data.study.procedureRuns[0];
    if (isInaccessible(procedureRun)) {
      return (
        <StudyInaccessibleContentBanner
          inaccessibleInfo={procedureRun}
          modelType={ModelTypeConstants.PROCEDURE_RUN}
          displayErrorText="The experiment run of this study is not accessible. The experiment run associated with this study may have been moved into a project that you do not have access to."
        />
      );
    } else if (isInaccessible(procedureRun.templateProcedureFlowchartVersion)) {
      return (
        <StudyInaccessibleContentBanner
          inaccessibleInfo={procedureRun.templateProcedureFlowchartVersion}
          modelType={ModelTypeConstants.PROCEDURE_RUN}
          displayErrorText="The procedure flowchart of this study is not accessible. The procedure flowchart associated with this study may have been moved into a template collection that you do not have access to."
        />
      );
    } else {
      const overview = buildProcessDevelopmentStudyExecutionProgress(procedureRun);
      return <RenderProcessDevelopmentStudyExecutionProgress overview={overview} />;
    }
  }
}

function RenderProcessDevelopmentStudyExecutionProgress({
  overview,
}: {
  overview: ProcedureRunOverview;
}): JSX.Element {
  // Track viewer state
  const [enabledStatusFilters, setEnabledStatusFilters] = useState(new Map());
  const setFilters = (statuses: Array<ProcessTaskStatusType>, isFilterSet: boolean): void => {
    const newEnabledStatusFilters = new Map(enabledStatusFilters);
    statuses.forEach((status) => newEnabledStatusFilters.set(status, isFilterSet));
    setEnabledStatusFilters(newEnabledStatusFilters);
  };

  // Calculate state values
  const statusCounts = getUnitOperationCountByTaskStatus(overview);

  return (
    <>
      <div className={css(styles.gridContainer)}>
        {/* ========== Header ========== */}
        <div style={{gridColumnStart: 1, gridColumnEnd: 6}} className={css(styles.cellHeader)}>
          Unit operations
        </div>
        {/* ========== Body ========== */}
        <RenderStatusCountPane
          statuses={[PLANNED]}
          statusCounts={statusCounts}
          enabledStatusFilters={enabledStatusFilters}
          setFilters={setFilters}
        />
        <RenderStatusCountPane
          statuses={[ProcessTaskStatusType.PENDING]}
          statusCounts={statusCounts}
          enabledStatusFilters={enabledStatusFilters}
          setFilters={setFilters}
        />
        <RenderStatusCountPane
          statuses={[ProcessTaskStatusType.IN_PROGRESS]}
          statusCounts={statusCounts}
          enabledStatusFilters={enabledStatusFilters}
          setFilters={setFilters}
        />
        <RenderStatusCountPane
          statuses={[ProcessTaskStatusType.COMPLETED]}
          statusCounts={statusCounts}
          enabledStatusFilters={enabledStatusFilters}
          setFilters={setFilters}
        />
        <RenderStatusCountPane
          statuses={[
            ProcessTaskStatusType.INVALID,
            ProcessTaskStatusType.SKIPPED,
            ProcessTaskStatusType.FAILED,
            ProcessTaskStatusType.CANCELLED,
          ]}
          statusCounts={statusCounts}
          enabledStatusFilters={enabledStatusFilters}
          setFilters={setFilters}
        />
      </div>
      <ProcedureRunParamsTable
        overview={overview}
        isMultiExperimentEnabled={false}
        enabledStatusFilters={enabledStatusFilters}
      />
    </>
  );
}

function RenderStatusCountPane({
  statuses,
  statusCounts,
  enabledStatusFilters,
  setFilters,
}: {
  statuses: Array<UnitOperationStatusType>;
  statusCounts: Map<UnitOperationStatusType, number>;
  enabledStatusFilters: Map<UnitOperationStatusType, boolean>;
  setFilters: (statuses: Array<UnitOperationStatusType>, isFilterSet: boolean) => void;
}): JSX.Element {
  const filterStatus = statuses.every((status) => enabledStatusFilters.get(status) ?? false);
  return (
    <div
      data-status-pane-id={statuses[0]}
      className={classNames(
        css(styles.roundedPane),
        css(styles.clickablePane),
        filterStatus ? css(styles.selectedPane) : null
      )}
      onClick={() => {
        setFilters(statuses, !filterStatus);
      }}
    >
      {statuses.length === 1 ? (
        <>
          <RenderTaskStatusWithIcon statusType={statuses[0]} />
          <div data-status-count-id={statuses[0]} className={css(styles.statusCount)}>
            {statusCounts.get(statuses[0]) ?? 0}
          </div>
        </>
      ) : (
        statuses.map((status) => (
          <div key={`status-count-${status}`} className={css(styles.miscStatusLine)}>
            <RenderTaskStatusWithIcon statusType={status} />
            <span data-status-count-id={status} className={css(styles.paneRight)}>
              {statusCounts.get(status) ?? 0}
            </span>
          </div>
        ))
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  loadingDiv: {
    paddingBottom: 5,
  },
  // Layout
  gridContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
    gap: '10px',
  },
  // Cell Styles
  cellHeader: {
    verticalAlign: 'top',
    fontWeight: 'bold',
  },
  statusCount: {
    fontSize: 'x-large',
    paddingTop: 20,
  },
  roundedPane: {
    height: 'inherit',
    borderRadius: 4,
    border: `1px solid ${Colors.GRAY_LIGHT}`,
    paddingTop: 10,
    paddingBottom: 10,
    paddingLeft: 15,
    paddingRight: 15,
  },
  clickablePane: {
    cursor: 'pointer',
    ':hover': {
      backgroundColor: Colors.GRAY_LIGHT,
    },
  },
  selectedPane: {
    borderColor: Colors.BLUE,
    backgroundColor: Colors.BLUE_POWDER_LIGHT,
  },
  paneRight: {
    float: 'right',
    paddingLeft: 10,
  },
  plannedIcon: {
    paddingLeft: 2,
    paddingRight: 7,
  },
  miscStatusLine: {
    paddingBottom: 2,
  },
  statusText: {
    paddingLeft: 4,
  },
  paramsTableInCard: {
    width: '97%',
  },
});
